"use client";

import StoreBox from "../store/store-box";
import ProductDetails from "./product-details";
import NotFound from "../common/NotFound";
import { useAppDispatch, useAppSelector } from "../slice/hooks";
import { getActiveCart } from "../slice/cart-slice";
import { useEffect, useMemo, useState } from "react";
import { CONFIG } from "../../lib/util/config";
import { useToken } from "../../lib/context/auth-context";
import styles from "./index.module.css";
import SimilarProducts from "./similarProducts";
import ProductPageSkeleton from "./product-page-skeleton";
import {
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
  setOrganisation,
} from "../slice/accessOrg-slice";
import { useHistoryManager } from "../history/useHistoryManager";

const ProductPage = (props) => {
  const cart = useAppSelector(getActiveCart);
  const {
    urlData,
    showCart,
    storePath,
    accessPointId,
    organisation,
    accessPoint,
    accessPoints,
    serverToken,
    navLocation,
  } = props;

  const { token } = useToken();

  /* =========================
     RESOLVE PRODUCT (🔥 SOURCE OF TRUTH)
     ========================= */

  const resolvedProduct = useMemo(() => {
    if (!urlData) return null;
    return urlData.product ?? urlData.product ?? null;
  }, [urlData]);

  const [productRes, setProductRes] = useState(urlData?.product ?? null);
  const [loading, setLoading] = useState(!resolvedProduct);

  const [isSwitching, setSwitching] = useState(false);
  const dispatch = useAppDispatch();

  /* =========================
     SYNC STATE FROM URL DATA
     ========================= */

  useEffect(() => {
    if (!urlData) return;

    // Restore organisation context from snapshot
    if (urlData.organisation) {
      dispatch(setOrganisation(urlData.organisation));
    }

    if (urlData.accessPointId) {
      dispatch(setAccessPointId(urlData.accessPointId));
    }

    if (urlData.accessPoint) {
      dispatch(setAccessPoint(urlData.accessPoint));
    }

    if (Array.isArray(urlData.accessPoints)) {
      dispatch(setAccessPoints(urlData.accessPoints));
    }

    if (urlData.product) {
      setProductRes(urlData?.product);
    }
  }, [urlData]);

  useEffect(() => {
    if (resolvedProduct) {
      setProductRes(resolvedProduct);
      setLoading(false);
      setSwitching(false);
    } else {
      setProductRes(null);
      setLoading(true);
    }
  }, [resolvedProduct]);

  /* =========================
     FETCH FALLBACK (ONLY IF NEEDED)
     ========================= */

  const checkSum = useMemo(() => {
    if (typeof window === "undefined") return null;
    const slug = window.location.pathname.split("/").filter(Boolean);
    const productIndex = slug.indexOf("product");
    if (productIndex === -1 || slug.length <= productIndex + 1) return null;
    return slug[productIndex + 1].split("-").pop();
  }, []);

  useEffect(() => {
    if (resolvedProduct) return;

    if (!checkSum || !accessPointId || !organisation) return;
    let cancelled = false;

    async function fetchProduct() {
      setLoading(true);

      try {
        const res = await fetch(
          `${CONFIG.apiUrl}/product/public/organisation/${organisation.orgId}/checksum/${checkSum}/info?useFeed=true&accessPointId=${accessPointId}`,
          {
            headers: {
              Authorization: `Bearer ${token || serverToken}`,
              "Content-Type": "application/json",
            },
            next: { revalidate: 10 },
          },
        );

        const json = res.ok ? await res.json() : null;

        if (!cancelled) {
          setProductRes(json?.data?.product || null);
        }
      } catch {
        if (!cancelled) setProductRes(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchProduct();
    return () => {
      cancelled = true;
    };
  }, [
    resolvedProduct,
    checkSum,
    accessPointId,
    organisation,
    token,
    serverToken,
  ]);

  /* =========================
     RENDER
     ========================= */

  return (
    <StoreBox {...props} storePath={storePath} showMarketDiv={true}>
      <div className={styles.productDetails}>

        {(loading || isSwitching) && <ProductPageSkeleton />}

        {!loading && !isSwitching && productRes && (
          <ProductDetails
            cart={cart}
            product={productRes}
            showCart={showCart}
            enablePreview
            accessPoint={accessPoint}
            organisation={organisation}
          />
        )}

        {!loading && !productRes && !isSwitching && <NotFound />}
      </div>

      <SimilarProducts
        product={productRes}
        setProduct={setProductRes}
        organisation={organisation}
        accessPoint={accessPoint}
        accessPoints={accessPoints}
        token={token || serverToken}
        storePath={storePath}
        isSwitching={isSwitching}
        setSwitching={setSwitching}
        navLocation={navLocation}
      />
    </StoreBox>
  );
};

export default ProductPage;
